import React from 'react'
import { Alert, AlertProps, Box, BoxProps, Link, Typography, useTheme } from '@mui/material'
import { MuiMarkdown, MuiMarkdownProps, defaultOverrides } from 'mui-markdown'

interface CodeElementProps {
  className?: string
  children?: React.ReactNode
  [key: string]: unknown
}
type CodeElement = React.ReactElement<CodeElementProps>

type MuiAlertPreProps = {
	children?: React.ReactNode
}
const MuiAlertPre: React.FC<MuiAlertPreProps> = ({ children }) => {
  // 型ガード: childrenが`<code>`要素であるか
  if (!React.isValidElement(children) || typeof children.type !== 'function' || children.type.name !== 'code') {
    return <pre>{children}</pre>
  }
	const codeElement = children as CodeElement
	const { className, children: codeContent } = codeElement.props

  // `className` から言語指定を抽出 (e.g., "lang-warn" -> "warn")
	const match = /lang-(\w+)/.exec(className || '')
  const lang = match ? match[1] : null

  const severityMap: { [key: string]: AlertProps['severity'] } = {
    warn: 'warning',
    note: 'info',
    success: 'success',
    error: 'error',
  }
  if (lang && lang in severityMap) {
    const severity = severityMap[lang]
    return (
      <Alert severity={severity}>
        {codeContent}
      </Alert>
    )
  }
  return <pre>{children}</pre>
}

type MarkdownPreviewProps = {
	value?: string | null
	emptyText?: string
	mdProps?: Omit<MuiMarkdownProps, "children" | "options">
	onLinkClick?: (url: string) => boolean
} & BoxProps
export const MarkdownPreview = ({
	value,
	emptyText,
	mdProps,
	onLinkClick,
	...others
}: MarkdownPreviewProps) => {
	const theme = useTheme()
	return (
		<Box padding={2} {...others}>
			{(value && value.length > 0) ? (
				<Box sx={{
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-all',
					color: theme.palette.text.primary,
					'& *': {
						fontSize: '0.875rem',
						lineHeight: 1.43
					},
					'& svg': {
						fontSize: '1.43rem',
					},
					'& h1': {
						fontSize: '1.2rem',
						fontWeight: 500,
						marginBottom: '0.5rem'
					},
					'& * + .MuiTypography-h1': {
						marginTop: '1.2rem'
					},
					'& h2': {
						fontSize: '1rem',
						fontWeight: 400,
						marginBottom: '0.5rem',
					},
					'& * + .MuiTypography-h2': {
						marginTop: '1rem'
					},
					'& h3': {
						fontSize: '0.875rem',
						marginBottom: '0.4rem',
						textDecoration: 'underline'
					},
					'& * + .MuiTypography-h3': {
						marginTop: '0.875rem'
					},
					'& h4': {
						fontSize: '0.875rem',
						fontWeight: 'bold',
						marginBottom: '0.4rem'
					},
					'& * + .MuiTypography-h4': {
						marginTop: '0.875rem'
					},
					'& li': {
						marginTop: '0.4rem',
						whiteSpace: 'normal'
					},
					'& ul': {
						paddingInlineStart: '1.5rem'
					},
					'& ul + *': {
						marginTop: '0.875rem'
					},
					'& ol': {
						paddingInlineStart: '2rem'
					},
					'& > ul:first-of-type > li:first-child': {
						marginTop: '0'
					},
					'& hr': {
						marginTop: '1rem',
						marginBottom: '1rem',
					}
				}}>
					<MuiMarkdown
						{...mdProps}
						overrides={{
							...defaultOverrides,
							pre: {
								component: MuiAlertPre
							},
							a: {
								component: Link,
								props: {
									target: '_blank',
									rel: 'noopener noreferrer',
									underline: 'hover',
									onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
										if (onLinkClick) {
											const href = e.currentTarget.getAttribute('href')
											if (href) {
												if (onLinkClick(href)) {
													e.preventDefault()
												}
											}
										}
									}
								}
							}
						}}
					>
						{value}
					</MuiMarkdown>
				</Box>
			) : emptyText ? (
				<Typography variant="body2" color="text.secondary" component='div'>{emptyText}</Typography>
			) : <></>}
		</Box>
	)
}